import type { TelemetryPort } from '@core/domain/ports/driven/TelemetryPort.ts';
import { createCorrelationId } from '@core/domain/utils/correlationId.ts';
import type { ShellPorts } from '@src/core/bootstrap/ShellPorts.ts';
import type { AuthApplicationPort } from '@src/modules/auth/domain/ports/AuthApplicationPort.ts';
import type { NotificationPort } from '@src/modules/platform/notifications/domain/ports/NotificationPort.ts';
import type { WebComponentsPresentationShellBase } from '@src/modules/shell-routing/infrastructure/drivers/routes/shellDomMount.ts';
import type { SystemApplicationService } from '@src/modules/system/application/SystemApplicationService.ts';
import {
  notifyPostSnapshotRevisionUpgrade,
  syncTelemetryUserContext,
  waitForLayoutComponent,
} from './shellSessionHelpers.ts';
import { runPostLoginCatalogSync } from './postLoginCatalogSync.ts';

export type AuthenticatedLoginOrchestration = {
  auth: AuthApplicationPort;
  telemetry: TelemetryPort;
  notifications: NotificationPort;
  system: SystemApplicationService;
  presentation: WebComponentsPresentationShellBase;
  getShellPorts: (auth: AuthApplicationPort) => ShellPorts;
  isRouterStarted: () => boolean;
  startRouter: () => void;
  navigateHome: () => void;
};

/**
 * Single post-login path: URL → layout → catalog sync (pull/push + offline warmup) → router.
 * Login UI calls only `notifyLoginSuccess()`.
 */
export async function orchestrateAuthenticatedLogin(deps: AuthenticatedLoginOrchestration): Promise<void> {
  globalThis.history.replaceState({}, '', '/');
  await syncTelemetryUserContext(deps.auth, deps.telemetry, true);

  const shellPorts = deps.getShellPorts(deps.auth);
  await deps.presentation.mountLayoutRootInApp(shellPorts);
  await waitForLayoutComponent();
  await runPostLoginCatalogSync(deps.system, deps.notifications, {
    entregaCatalog: shellPorts.entrega.catalog,
  });

  if (!deps.isRouterStarted()) {
    notifyPostSnapshotRevisionUpgrade(shellPorts);
    deps.telemetry.logInfo('Bootstrap: router', { correlationId: createCorrelationId() });
    deps.startRouter();
  } else {
    deps.navigateHome();
  }
}

export { waitForLayoutComponent } from './shellSessionHelpers.ts';
